import { BILLING_FEATURE_CUSTOM_CONFIG, TValidBillingTypes } from "@llws/hydra-shared";
import { AccordionDetails, AccordionSummary, Typography } from "@mui/material";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import { ISubscriptions } from '@llws/lift-entity-interfaces';
import { useMemo, useState } from "react";
import { StyledAccordion } from "src/components/pages/Subscriptions/tiered-unit-count/styledComponents";
import { Nullable } from "@jamesgmarks/utilities";
import { DailyRateConfigurationAccordion, DailyRateConfigurationEditor } from "src/components/pages/Subscriptions/form/CustomConfigEditor/DailyRateConfigurationEditor";
import { TCustomConfiguration } from "src/components/pages/Subscriptions/form/CustomConfigEditor/customConfigMeta";
import { ActiveFeatureConfigurationAccordion } from "src/components/pages/Subscriptions/form/CustomConfigEditor/ActiveFeatureConfigurationEditor";
import { PromotedListingsConfigurationAccordion } from "src/components/pages/Subscriptions/form/CustomConfigEditor/PromotedListingsConfigurationEditor";
import { TieredUnitCountConfigurationAccordion } from "src/components/pages/Subscriptions/form/CustomConfigEditor/TieredUnitCountConfigurationEditor";

interface ICustomConfigEditorProps {
  customConfiguration: Nullable<TCustomConfiguration>;
  baseCustomConfiguration: Nullable<TCustomConfiguration>;
  storeSubscription: Nullable<ISubscriptions>;
  componentName: string;
  onSave: (config: TCustomConfiguration) => void;
  disabled?: boolean;
  hasErrors?: boolean;
}

const editorMap: Record<
  string,
  (props: {
    isEditing: boolean
    setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
    baseConfig: Nullable<TCustomConfiguration>;
    subConfig: Nullable<TCustomConfiguration>;
    storeSubscription: Nullable<ISubscriptions>;
    onSave: (config: TCustomConfiguration) => void;
  }) => JSX.Element
> = {
  'dailyRateConfigurationEditor': ({
    isEditing,
    setIsEditing,
    baseConfig,
    subConfig,
    storeSubscription,
    onSave,
  }) => (
    <DailyRateConfigurationAccordion
      isEditingSubscriptionConfiguration={isEditing}
      setIsEditingSubscriptionConfiguration={setIsEditing}
      baseSubscriptionConfiguration={baseConfig}
      subscriptionConfiguration={subConfig}
      setCustomConfiguration={(config) => {
        onSave(config);
      }}
      storeSubscription={storeSubscription}
    />
  ),
  'tieredUnitCountConfigurationEditor': ({
    isEditing,
    setIsEditing,
    baseConfig,
    subConfig,
    storeSubscription,
    onSave,
  }) => (
    <TieredUnitCountConfigurationAccordion
      isEditingSubscriptionConfiguration={isEditing}
      setIsEditingSubscriptionConfiguration={setIsEditing}
      baseSubscriptionConfiguration={baseConfig}
      subscriptionConfiguration={subConfig}
      setCustomConfiguration={(config) => {
        onSave(config);
      }}
      storeSubscription={storeSubscription}
    />
  ),
  'activeFeatureConfigurationEditor': ({
    isEditing,
    setIsEditing,
    baseConfig,
    subConfig,
    storeSubscription,
    onSave,
  }) => (
    <ActiveFeatureConfigurationAccordion
      isEditingSubscriptionConfiguration={isEditing}
      setIsEditingSubscriptionConfiguration={setIsEditing}
      baseSubscriptionConfiguration={baseConfig}
      subscriptionConfiguration={subConfig}
      setCustomConfiguration={(config) => {
        onSave(config);
      }}
      storeSubscription={storeSubscription}
    />
  ),
  'promotedListingsConfigurationEditor': ({
    isEditing,
    setIsEditing,
    baseConfig,
    subConfig,
    storeSubscription,
    onSave,
  }) => (
    <PromotedListingsConfigurationAccordion
      isEditingSubscriptionConfiguration={isEditing}
      setIsEditingSubscriptionConfiguration={setIsEditing}
      baseSubscriptionConfiguration={baseConfig}
      subscriptionConfiguration={subConfig}
      setCustomConfiguration={(config) => {
        onSave(config);
      }}
      storeSubscription={storeSubscription}
    />
  ),
};

export const CustomConfigEditor: React.FC<ICustomConfigEditorProps> = ({
  customConfiguration,
  baseCustomConfiguration,
  storeSubscription,
  componentName = '',
  onSave,
  disabled = false,
  hasErrors = false,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [isEditingSubscriptionConfiguration, setIsEditingSubscriptionConfiguration] = useState(false);

  const availableComponents = useMemo(() => Object.keys(editorMap), []);

  const isDisabled = (
    (!baseCustomConfiguration && !customConfiguration)
    || disabled
    || !availableComponents.includes(componentName)
  );

  const renderEditor = () => {
    const renderFn = editorMap[componentName];
    if (!renderFn) return null;

    return renderFn({
      isEditing: isEditingSubscriptionConfiguration,
      setIsEditing: setIsEditingSubscriptionConfiguration,
      baseConfig: baseCustomConfiguration,
      subConfig: customConfiguration,
      storeSubscription: storeSubscription,
      onSave: (config) => {
        onSave(config);
      },
    });
  };

  return (
    <StyledAccordion
      hasErrors={hasErrors && !disabled}
      disabled={isDisabled}
      expanded={expanded && !disabled}
      onChange={() => setExpanded((old) => !old)}
    >
      <AccordionSummary expandIcon={<GridExpandMoreIcon sx={{ fontSize: '2rem' }} />}>
        <Typography variant="h6">Custom configuration...</Typography>
      </AccordionSummary>
      <AccordionDetails>{renderEditor()}</AccordionDetails>
    </StyledAccordion>
  );
};